<script setup>
import { cloneDeep, isEmpty } from 'lodash';
import {
  watch, ref, nextTick, reactive, computed,
} from 'vue';
import { modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import ReferenciasForm from './ReferenciasForm.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  error: { type: Object, default: null },
});

const emits = defineEmits(['update:modelValue']);

const actividades = reactive([...cloneDeep(props.modelValue)]);
const nombreActividad = ref(null);
const actividadesNoEliminadas = computed(() => actividades?.filter((ac) => !ac._destroy));

function eliminarActividad(index) {
  if (index < 0) { return; }

  const actividad = actividades[index];

  if (actividad !== null) {
    if (actividad.id == null) {
      actividades.splice(index, 1);
    } else {
      actividad._destroy = true;
    }
  }
}

function addActividad() {
  actividades.push({
    nombre: null,
    descripcion: null,
    fecha_inicio: null,
    fecha_fin: null,
    fecha_entrega: null,
    horas: 0,
  });
  nextTick(() => {
    nombreActividad.value[actividades.length - 1].focus();
  });
}

function addReferencias(actividad, referencias) {
  actividad.referencias = referencias.filter((enlace) => !isEmpty(enlace));
}

watch(actividades, () => {
  emits('update:modelValue', actividades);
});
</script>

<template>
  <div class="fila-botones">
    <div class="boton m-b">
      <button
        type="button"
        class="btn btn-xs btn-cifra"
        @click="addActividad()"
      >
        <i class="fa fa-plus"></i>
        {{ $I18n.t('actividad') }}
      </button>
    </div>
  </div>
  <div v-for="(actividad, index) in actividadesNoEliminadas" :key="index" class="m-b">
    <div class="panel panel-default">
      <div class="panel-heading clearfix">
        <div class="panel-title">
          <div class="botones pull-right">
            <i
              class="fa fa-trash text-danger clickable"
              @click="eliminarActividad(index)"
            ></i>
          </div>
        </div>
      </div>
      <div class="panel-body">
        <div class="form-group">
          <input-base
            ref="nombreActividad"
            v-model="actividad.nombre"
            :label-key="$I18n.t(
              'activerecord.attributes.lomloe/situacion_aprendizaje_actividad.nombre'
            )"
            :error="error['actividades.nombre']"
            :required="true"
          />
        </div>
        <div class="form-group">
          <label class="control-label">
            {{
              $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje_actividad.descripcion')
            }}
          </label>
          <textarea
            v-model="actividad.descripcion"
            class="form-control"
            rows="4"
          ></textarea>
          <p v-if="error['actividades.descripcion']" class="text-danger">
            {{ modelErrorsToHtmlText(error['actividades.descripcion']) }}
          </p>
        </div>
        <div class="fecha-hora">
          <div class="fecha-inicio form-group">
            <input-fecha-hora
              v-model="actividad.fecha_inicio"
              :error="error['actividades.fecha_inicio']"
              :label="$I18n.t(
                'activerecord.attributes.lomloe/situacion_aprendizaje_actividad.fecha_inicio'
              )"
              :required="true"
            />
          </div>
          <div class="fecha-fin form-group">
            <input-fecha-hora
              v-model="actividad.fecha_fin"
              :error="error['actividades.fecha_fin']"
              :label="$I18n.t(
                'activerecord.attributes.lomloe/situacion_aprendizaje_actividad.fecha_fin'
              )"
            />
          </div>
          <div class="horas form-group">
            <label for="" class="control-label">
              {{ $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje_actividad.horas') }}
            </label>
            <selector-numerico
              v-model.number="actividad.horas"
              class="input-3-digitos unidades"
              size="sm"
              :min="0"
              :max="999"
            >
            </selector-numerico>
          </div>
          <div class="fecha-entrega form-group">
            <input-fecha-hora
              v-model="actividad.fecha_entrega"
              :error="error['actividades.fecha_entrega']"
              :label="$I18n.t(
                'activerecord.attributes.lomloe/situacion_aprendizaje_actividad.fecha_entrega'
              )"
            />
          </div>
        </div>
        <div class="form-group archivo">
          <div v-if="actividad.id == null" class="text-info">
            {{ $I18n.t('situaciones_aprendizaje_aviso_guardar') }}
          </div>
        </div>
        <dropzone
          :disabled="actividad.id == null"
          modelo="SituacionAprendizajeActividad"
          :id-modelo="actividad.id"
        >
        </dropzone>
        <div v-if="error['actividades.archivos.archivo']" class="text-danger m-t-xs">
          {{ modelErrorsToHtmlText(error['actividades.archivos.archivo']) }}
        </div>
        <ReferenciasForm
          :model-value="actividad.referencias"
          :error="error['actividades.referencias']"
          @update:model-value="addReferencias(actividad, $event)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .fila-botones {
    display: flex;
    .titulo-lg, .titulo-md, .titulo-xs {
      font-weight: 600;
    }
    .titulo-lg {
      font-size: 14px;
    }
    .titulo-md {
      font-size: 13px;
    }
    .titulo-xs {
      font-size: 12px;
    }
    .boton {
      text-align: right;
      flex: 1;
    }
  }

  textarea {
    border-radius: 10px;
  }

  abbr {
    border: 0;
    text-decoration: none;
    color: #fe1d12;
  }

  .fecha-hora {
    display: grid;
    grid-template-columns: auto auto auto auto;
    column-gap: 10px;
    padding-bottom: 1em;
    align-items: center;
  }
</style>
